<template>
  <v-container fluid>
    <section>
      <!-- page header -->

      <!-- PAGE HEADER -->
      <div class="ap-header-row mb-5 d-flex align-center justify-space-between flex-wrap" style="gap: 16px;">
        <div class="d-flex align-center" style="gap: 12px;">
          <v-btn
            depressed
            outlined
            class="ap-back-btn rounded-lg"
            @click="goBack"
          >
            <v-icon small color="primary">fal fa-arrow-left</v-icon>
          </v-btn>
          <div>
            <div class="d-flex align-center" style="gap: 8px;">
              <h1 class="product-page-title font-weight-black mb-0">
                {{ headline }}
              </h1>
              <v-chip
                small
                label
                :color="readinessCount === readinessTotal ? 'success' : 'primary'"
                class="font-weight-bold white--text"
              >
                {{ readinessCount === readinessTotal ? $t("ready") : `${readinessCount}/${readinessTotal}` }}
              </v-chip>
            </div>
            <div class="text-caption grey--text mt-1">
              {{ $t("create_product_subtitle") }}
            </div>
          </div>
        </div>

        <div class="d-flex align-center" style="gap: 14px;">
          <div class="ap-progress d-none d-md-block" style="min-width: 140px;">
            <div class="d-flex justify-space-between ap-progress__labels mb-1">
              <span class="text-caption grey--text">{{ $t("ready_to_save") }}</span>
              <span class="text-caption font-weight-bold">{{ readinessCount }} / {{ readinessTotal }}</span>
            </div>
            <v-progress-linear
              :value="readinessPercent"
              height="4"
              rounded
              color="primary"
              background-color="grey lighten-3"
            />
          </div>

          <!-- Quick top actions on desktop -->
          <div class="d-none d-sm-flex align-center" style="gap: 10px;">
            <v-btn
              depressed
              outlined
              color="error"
              class="text-none rounded-lg font-weight-bold px-4"
              @click="showClearConfirm = true"
            >
              <v-icon left small>fal fa-trash</v-icon>
              {{ $t("clear_form") }}
            </v-btn>
            <v-btn
              depressed
              color="primary"
              class="text-none rounded-lg font-weight-bold px-5 white--text"
              :loading="overlay"
              :disabled="overlay"
              @click="submitForm(form)"
            >
              <v-icon left small>fal fa-save</v-icon>
              {{ $t("save") }}
            </v-btn>
          </div>
        </div>
      </div>

      <v-row>
        <!-- ============ LEFT: IDENTITY / PRICE / INVENTORY ============ -->
        <div class="col-ms-12 col-md-8 col-lg-8 col-xl-8">
          <v-card class="ap-card mb-4" outlined>
            <v-card-title class="pb-1 product-card-title ap-card-title">
              <span class="ap-card-accent"></span>
              <span>{{ $t("identity") }}</span>
              <span class="ap-card-subtitle">{{
                $t("identity_subtitle")
              }}</span>
            </v-card-title>
            <v-card-text class="pa-7 pt-3">
              <!-- image + localized name, side by side -->
              <div class="ap-identity-row mb-5">
                <div class="ap-identity-row__image">
                  <div class="section-label mb-2">{{ $t("product_image") }}</div>
                  <DragDropUpload
                    v-model="imgFile"
                    accept="image/*"
                    :max-size-mb="5"
                    @file-selected="onFileSelected"
                    @file-removed="onFileRemoved"
                  />
                  <div class="ap-hint mt-1">{{ $t("image_upload_hint") }}</div>
                </div>

                <div class="ap-identity-row__name">
                  <div class="section-label mb-2">{{ $t("product_name") }} <span class="required-mark">*</span></div>
                  <v-btn-toggle
                    v-model="activeLang"
                    mandatory
                    dense
                    class="ps-status-toggle mb-3"
                  >
                    <v-btn
                      v-for="lang in langTabs"
                      :key="lang.code"
                      small
                      :value="lang.code"
                    >
                      {{ lang.code }}
                      <span
                        class="ap-lang-dot ml-1"
                        :class="{ 'ap-lang-dot--filled': lang.filled }"
                      ></span>
                    </v-btn>
                  </v-btn-toggle>
                  <TTextField
                    skip-translation
                    dense
                    v-model="currentNameValue"
                    :errorMessages="activeLang === 'LA' ? nameError : []"
                    @input="activeLang === 'LA' && $v.form.name.$touch()"
                    @blur="activeLang === 'LA' && $v.form.name.$touch()"
                  />

                  <!-- classification: stacked below the name field, beside the image -->
                  <v-row class="mt-3" dense>
                    <v-col cols="12" sm="6">
                      <TTextField
                        v-model="form.code"
                        label="barcode"
                        dense
                        :counter="true"
                        :maxlength="20"
                        :errorMessages="codeError"
                        @input="$v.form.code.$touch()"
                        @blur="$v.form.code.$touch()"
                      >
                        <template v-slot:append>
                          <v-btn
                            fab
                            x-small
                            depressed
                            class="barcode-gen-btn"
                            @click="generateBarcode"
                            :title="
                              $t('generate_barcode') || 'Generate Barcode'
                            "
                          >
                            <v-icon x-small>fal fa-barcode-scan</v-icon>
                          </v-btn>
                        </template>
                      </TTextField>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <TextSelect
                        label="category"
                        dense
                        :errorMessages="category_idError"
                        v-model="form.category_id"
                        @input="$v.form.category_id.$touch()"
                        @blur="$v.form.category_id.$touch()"
                        itemValue="id"
                        text="la_name"
                        :items="categories"
                      />
                      <span
                        class="ap-hint ap-hint--link"
                        @click="openQuickCategoryModal"
                        >+ {{ $t("add_category") }}</span
                      >
                    </v-col>
                    <v-col cols="12" sm="6">
                      <TextSelect
                        label="unit"
                        dense
                        v-model="form.size_id"
                        itemValue="id"
                        text="name"
                        :items="sizes"
                      />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <TextSelect
                        label="supplier"
                        dense
                        v-model="form.vendor_id"
                        itemValue="id"
                        text="name"
                        :items="suppliers"
                      />
                    </v-col>
                  </v-row>

                  <div class="mt-3">
                    <TextArea
                      label="description"
                      dense
                      v-model="form.desc"
                      :rows="showOnlineMedia ? 6 : 3"
                    />
                    <div
                      v-if="showOnlineMedia"
                      class="text-caption grey--text mt-1"
                    >
                      {{ $t("product_desc_markdown_hint") }}
                    </div>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <v-card class="ap-card mb-4" outlined>
            <v-card-title class="pb-1 product-card-title ap-card-title">
              <span class="ap-card-accent"></span>
              <span>{{ $t("price") }}</span>
              <span class="ap-card-subtitle">{{ $t("price_subtitle") }}</span>
            </v-card-title>
            <v-card-text class="pa-7 pt-3">
              <!-- currency -->
              <div class="section-label mb-2">{{ $t("currency") }}</div>
              <v-btn-toggle
                v-model="form.currency_id"
                mandatory
                dense
                class="ps-status-toggle mb-4"
              >
                <v-btn
                  v-for="cur in currencies"
                  :key="cur.id"
                  small
                  :value="cur.id"
                >
                  <v-avatar size="14" class="mr-1" v-if="cur.flag">
                    <v-img :src="cur.flag" />
                  </v-avatar>
                  {{ cur.code }}
                </v-btn>
              </v-btn-toggle>

              <!-- buy / sale prices -->
              <div class="row">
                <div class="col-6 pa-1">
                  <CurrencyInput
                    v-model="form.price_buy"
                    :suffix="currency"
                    label="cost"
                    dense
                    hide-details
                  />
                </div>
                <div class="col-6 pa-1">
                  <CurrencyInput
                    v-model="form.price_sale"
                    :suffix="currency"
                    label="price_sale"
                    dense
                    hide-details
                  />
                </div>
              </div>

              <!-- profit -->
              <v-row>
                <v-col cols="12" class="px-0">
                  <div class="v2-profit">
                    <div class="v2-profit__info">
                      <span class="v2-profit__label">{{
                        $t("profit_per_unit")
                      }}</span>
                      <span class="v2-profit__margin">{{ marginNote }}</span>
                    </div>
                    <span
                      class="v2-profit__value"
                      :class="
                        price_sale - price_buy >= 0
                          ? 'success--text'
                          : 'error--text'
                      "
                    >
                      {{ price_sale - price_buy >= 0 ? "+" : ""
                      }}{{ (price_sale - price_buy) | NumberFormat }}
                    </span>
                  </div>
                </v-col>
              </v-row>
              <div class="ap-hint mt-2">
                <v-icon x-small class="mr-1" color="grey"
                  >fal fa-info-circle</v-icon
                >
                {{ $t("margin_recalc_hint") }}
              </div>
            </v-card-text>
          </v-card>

          <!-- inventory -->
          <v-card class="ap-card mb-4" outlined>
            <v-card-title class="pb-1 product-card-title ap-card-title">
              <span class="ap-card-accent"></span>
              <span>{{ $t("inventory") }}</span>
              <span class="ap-card-subtitle">{{
                $t("inventory_subtitle")
              }}</span>
            </v-card-title>
            <v-card-text class="pa-7 pt-3">
              <div class="ap-toggle-row mb-4">
                <div class="v2-toggle-row__text">
                  <p class="v2-toggle-title">{{ $t("track_stock") }}</p>
                  <p class="v2-toggle-desc">{{ $t("track_stock_desc") }}</p>
                </div>
                <v-switch
                  v-model="form.track_stock"
                  inset
                  hide-details
                  color="primary"
                  :disabled="stockFieldsDisabled"
                />
              </div>

              <v-row v-if="form.track_stock && !stockFieldsDisabled" dense>
                <v-col cols="12" sm="4">
                  <CurrencyInput
                    v-model="form.stock"
                    label="stock"
                    dense
                    hide-details
                    :disabled="!edit_stock"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <CurrencyInput
                    v-model="form.notify_stock"
                    label="stock_notify"
                    dense
                    hide-details
                    typeShow="number"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="ps-text-field">
                    <div class="pb-2">{{ $t("stock_value") }}</div>
                    <div class="ap-readonly-box">
                      {{ stockValue | NumberFormat }} {{ currency }}
                    </div>
                  </div>
                </v-col>
              </v-row>

              <div v-if="stockFieldsDisabled" class="ap-hint mt-2">
                <v-icon x-small class="mr-1" color="grey"
                  >fal fa-info-circle</v-icon
                >
                {{ $t("stock_managed_at_variant_level") }}
              </div>
            </v-card-text>
          </v-card>

          <!-- online media (only when store joined) -->
          <ProductOnlineMedia
            v-if="showOnlineMedia"
            ref="productOnlineMedia"
            :existing-images.sync="galleryExisting"
            :pending-files.sync="galleryPendingFiles"
            :video-url.sync="videoUrl"
            :is-online.sync="form.is_online"
          />
        </div>

        <!-- ============ RIGHT: PREVIEW / AVAILABILITY / SETTINGS ============ -->
        <div class="col-ms-12 col-md-4 col-lg-4 col-xl-4">
          <!-- LIVE PRODUCT PREVIEW & AVAILABILITY CARD -->
          <v-card class="ap-card mb-4" outlined elevation="0">
            <v-card-title class="py-3 px-4 d-flex align-center justify-space-between border-bottom ap-card-title">
              <div class="d-flex align-center">
                <span class="ap-card-accent mr-2"></span>
                <span class="font-weight-bold text-subtitle-2">{{ $t("status_and_preview") || "Status & Preview" }}</span>
              </div>
              <v-chip x-small label :color="form.is_online ? 'success' : 'grey'" class="font-weight-bold white--text">
                {{ form.is_online ? $t("online") : $t("offline") }}
              </v-chip>
            </v-card-title>
            <v-card-text class="pa-4">
              <!-- Online switch row -->
              <div class="enable-switch-row mb-4 pa-3 rounded-lg bg-slate-50">
                <div>
                  <div class="font-weight-bold text-body-2">{{ $t("online") }}</div>
                  <div class="text-caption grey--text">{{ $t("online_product_desc") }}</div>
                </div>
                <v-switch
                  v-model="form.is_online"
                  inset
                  color="primary"
                  hide-details
                  class="enable-switch-control ma-0 pa-0 flex-grow-0 flex-shrink-0"
                />
              </div>

              <!-- LIVE PRODUCT PREVIEW CARD -->
              <div class="ps-product-preview-card mb-4">
                <div class="ps-product-preview-top d-flex align-center justify-space-between mb-3">
                  <div class="d-flex align-center">
                    <v-icon x-small color="primary" class="mr-1">fal fa-box</v-icon>
                    <span class="ps-product-preview-brand">PRODUCT PREVIEW</span>
                  </div>
                  <span class="ps-product-preview-readiness">
                    {{ readinessCount }} / {{ readinessTotal }}
                  </span>
                </div>

                <!-- Media + Name + Barcode -->
                <div class="d-flex align-start mb-3" style="gap: 12px;">
                  <div class="ps-product-preview-avatar">
                    <v-img v-if="previewImageSrc" :src="previewImageSrc" contain height="56" width="56" class="rounded-lg" />
                    <v-icon v-else size="26" color="primary">fal fa-box-open</v-icon>
                  </div>
                  <div class="flex-grow-1 overflow-hidden">
                    <div class="ps-product-preview-title text-truncate font-weight-bold">
                      {{ headline }}
                    </div>
                    <div v-if="form.code" class="d-flex align-center mt-1">
                      <span class="ps-product-preview-code">{{ form.code }}</span>
                    </div>
                    <div class="d-flex align-center flex-wrap mt-1" style="gap: 4px;">
                      <v-chip v-if="previewCategoryName" x-small outlined color="primary" class="font-weight-medium">
                        {{ previewCategoryName }}
                      </v-chip>
                      <v-chip v-if="previewUnitName" x-small outlined class="font-weight-medium">
                        {{ previewUnitName }}
                      </v-chip>
                    </div>
                  </div>
                </div>

                <div class="ps-product-preview-divider my-2"></div>

                <!-- Price & Profit Breakdown -->
                <div class="d-flex align-center justify-space-between py-1">
                  <span class="text-caption grey--text">{{ $t("price_sale") }}</span>
                  <span class="font-weight-black text-body-1 primary--text">
                    {{ price_sale | NumberFormat }} {{ currency }}
                  </span>
                </div>

                <div v-if="price_buy" class="d-flex align-center justify-space-between py-1">
                  <span class="text-caption grey--text">{{ $t("cost") }}</span>
                  <span class="text-caption font-weight-bold grey--text">
                    {{ price_buy | NumberFormat }} {{ currency }}
                  </span>
                </div>

                <div v-if="price_sale" class="d-flex align-center justify-space-between py-1">
                  <span class="text-caption grey--text">{{ $t("profit_per_unit") }}</span>
                  <span
                    class="text-caption font-weight-bold"
                    :class="price_sale - price_buy >= 0 ? 'success--text' : 'error--text'"
                  >
                    {{ price_sale - price_buy >= 0 ? "+" : "" }}{{ (price_sale - price_buy) | NumberFormat }} {{ currency }}
                    <span v-if="marginNote" class="text-caption grey--text">({{ marginNote }})</span>
                  </span>
                </div>

                <div class="ps-product-preview-footer mt-2 pt-2 border-top d-flex align-center justify-space-between text-caption grey--text">
                  <span>
                    <template v-if="form.track_stock">
                      <v-icon x-small class="mr-1" color="primary">fal fa-warehouse</v-icon>
                      {{ form.stock || 0 }} {{ $t("in_stock") || "in stock" }}
                    </template>
                    <template v-else>
                      {{ $t("no_stock_tracking") || "Stock not tracked" }}
                    </template>
                  </span>
                  <span v-if="form.is_online" class="success--text font-weight-medium">● {{ $t("online") }}</span>
                  <span v-else class="grey--text font-weight-medium">○ {{ $t("offline") || "In-Store Only" }}</span>
                </div>
              </div>

              <!-- Readiness Checklist -->
              <div class="ap-checklist-title mb-2">{{ $t("before_you_save") }}</div>
              <div class="ap-checklist">
                <div
                  v-for="check in readinessChecklist"
                  :key="check.key"
                  class="ap-checklist__row"
                >
                  <span
                    class="ap-checklist__mark"
                    :class="{ 'ap-checklist__mark--ok': check.ok }"
                  >
                    <v-icon v-if="check.ok" size="10" color="white"
                      >fal fa-check</v-icon
                    >
                  </span>
                  <span
                    class="ap-checklist__label"
                    :class="{ 'ap-checklist__label--ok': check.ok }"
                    >{{ check.label }}</span
                  >
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- settings: currency / expiry / inventory / variants / bundle / dynamic pricing -->
          <ProductChannelPrices
            v-model="form.channel_prices"
            :currency="currency"
            class="mb-4"
          />
          <div class="ap-settings-wrap">
            <ProductSettings
              ref="productSettings"
              :form="form"
              :currencies="currencies"
              :expiredPro.sync="expiredPro"
              :hasVariations.sync="hasVariations"
              :bundleItems.sync="bundleItems"
              :productId="tempProductId"
              :edit_stock="edit_stock"
              :options="options"
              :notify_stockError="productSettingsNotifyError"
              :show-stock="false"
              :show-currency="false"
              @variations-updated="handleVariationsUpdated"
            />
          </div>
        </div>
      </v-row>

      <!-- ============ ACTION BAR ============ -->
      <v-card class="ap-card mt-4 mb-6" outlined elevation="0">
        <v-card-actions class="pa-4 d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
          <p
            class="text-caption grey--text font-weight-bold mb-0 mr-auto hidden-sm-and-down"
          >
            {{ footerReadinessText }}
          </p>
          <div class="d-flex align-center flex-wrap" style="gap: 10px;">
            <v-btn
              v-if="canPrintBarcode"
              depressed
              outlined
              class="rounded-lg px-4 font-weight-bold"
              @click="openQuickBarcodeModal"
            >
              <v-icon left small>fal fa-barcode</v-icon>
              {{ $t("barcode_print") || "Barcode Print" }}
            </v-btn>
            <v-btn
              depressed
              outlined
              color="error"
              class="rounded-lg px-4 font-weight-bold"
              @click="showClearConfirm = true"
            >
              <v-icon left small>fal fa-trash</v-icon>
              {{ $t("clear_form") }}
            </v-btn>
            <v-btn
              depressed
              color="primary"
              class="rounded-lg px-6 font-weight-bold white--text form-action-btn-primary"
              :loading="overlay"
              :disabled="overlay"
              @click="submitForm(form)"
            >
              <v-icon left small>fal fa-save</v-icon>
              {{ $t("save") }}
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>

      <!-- clear form confirm -->
      <app-custom-dialog
        v-model="showClearConfirm"
        title="confirm_clear_form"
        width="460"
        cancelText="no"
        submitText="yes"
        @submit="
          clearData();
          showClearConfirm = false;
        "
      >
        <div slot="activator">
          <p class="ma-0">{{ $t("are_you_sure_clear_form") }}</p>
        </div>
      </app-custom-dialog>

      <!-- Quick Add Category Dialog -->
      <app-custom-dialog
        v-model="showQuickCategoryModal"
        title="add_category"
        width="460"
        submitText="add"
        :submit-loading="quickCategorySubmitting"
        @submit="handleQuickCategorySubmit"
      >
        <div slot="activator" class="pt-1">
          <TTextField
            label="la_name"
            v-model="quickCategoryForm.la_name"
            :errorMessages="quickCategoryError"
            @input="quickCategoryTouched = true"
          />
          <TTextField
            class="mt-3"
            label="en_name"
            v-model="quickCategoryForm.en_name"
          />
        </div>
      </app-custom-dialog>

      <!-- barcode preview modal -->
      <barcode-preview-modal
        v-model="showBarcodeModal"
        :product="previewProduct"
        @print="handleBarcodePrint"
      />
      <app-loading :overlay="overlay" />
    </section>
  </v-container>
</template>

<script>
import TTextField from "@/components/global/TextField";
import TextSelect from "@/components/global/TextSelect";
import FileInput from "@/components/global/FileInput";
import CurrencyInput from "@/components/CurrencyInput";
import TextArea from "@/components/global/TextArea";
import { mapGetters, mapActions } from "vuex";
import AppPreview from "@/components/products/preview";
import ProductVariations from "@/components/products/ProductVariations";
import ProductBundleBuilder from "@/components/products/ProductBundleBuilder.vue";
import ProductSettings from "@/components/products/ProductSettings.vue";
import ProductChannelPrices from "@/components/products/ProductChannelPrices.vue";
import ProductOnlineMedia from "@/components/products/ProductOnlineMedia.vue";
import { addProduct } from "@/validations/shop";
import { myMixin } from "@/mixin/main";
import { success, confirm, toast } from "@/controllers/alert";
import DragDropUpload from "@/components/global/DragDropUpload.vue";
import BarcodePreviewModal from "@/components/BarcodePreviewModal";
const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
  .toISOString()
  .substr(0, 10);
export default {
  mixins: [addProduct, myMixin],
  data: () => ({
    step: 1,
    imgFile: null,
    expiredPro: false,
    overlay: false,
    hasVariations: false,
    tempProductId: null,
    options: {
      precision: 0,
    },
    form: {
      code: "",
      name: "",
      name_en: "",
      name_th: "",
      name_cn: "",
      desc: "",
      stock: 0,
      expired_date: today,
      notify_stock: 10,
      track_stock: true,
      price_buy: 0,
      price_sale: 0,
      category_id: null,
      currency_id: 1,
      vendor_id: null,
      size_id: null,
      is_bundle: false,
      dynamic_pricing: false,
      channel_prices: [],
      is_online: true,
    },
    bundleItems: [],
    currencies: [],
    bundleSearch: "",
    bundleSearchResult: null,
    bundleQty: 1,
    showClearConfirm: false,
    showQuickCategoryModal: false,
    quickCategorySubmitting: false,
    quickCategoryTouched: false,
    quickCategoryForm: {
      la_name: "",
      en_name: "",
    },
    edit_stock: false,
    activeLang: "LA",
    productSettingsNotifyError: "",
    // Store variants and options data
    variantsData: [],
    optionsData: [],
    showBarcodeModal: false,
    storeJoinedOnline: false,
    branchJoinedOnline: false,
    galleryExisting: [],
    galleryPendingFiles: [],
    videoUrl: "",
  }),
  components: {
    FileInput,
    TextSelect,
    TTextField,
    TextArea,
    CurrencyInput,
    AppPreview,
    ProductVariations,
    ProductBundleBuilder,
    ProductSettings,
    ProductChannelPrices,
    ProductOnlineMedia,
    DragDropUpload,
    BarcodePreviewModal,
  },
  computed: {
    quickCategoryError() {
      if (!this.quickCategoryTouched) return [];
      if (!this.quickCategoryForm.la_name || !this.quickCategoryForm.la_name.trim()) {
        return [this.$t("validation_required")];
      }
      return [];
    },
    ...mapGetters({
      category: "index/category",
      suppliersData: "index/suppliers",
      sizes: "index/sizes",
    }),
    categories() {
      if (
        this.category != null &&
        this.category != undefined &&
        this.category.length
      ) {
        return this.category;
      } else {
        return [];
      }
    },
    suppliers() {
      if (
        this.suppliersData != null &&
        this.suppliersData != undefined &&
        this.suppliersData.length
      ) {
        return this.suppliersData;
      } else {
        return [];
      }
    },

    rate() {
      // Simplified rate calculation - return 1 as default
      return 1;
    },

    price_buy() {
      // Simplified price calculation
      if (this.form && this.form.price_buy) {
        return Number(this.form.price_buy);
      } else {
        return 0;
      }
    },

    price_sale() {
      // Simplified price calculation
      if (this.form && this.form.price_sale) {
        return Number(this.form.price_sale);
      } else {
        return 0;
      }
    },
    currency() {
      const found = this.currencies.find(
        (element) => element.id === this.form.currency_id,
      );
      if (found) {
        return found.code;
      }
      return "";
    },

    // Check if stock fields should be disabled
    stockFieldsDisabled() {
      return this.hasVariations || this.form.is_bundle;
    },

    // Check if all required fields are filled for barcode printing
    canPrintBarcode() {
      return (
        this.form.code &&
        this.form.code.trim() !== "" &&
        this.form.name &&
        this.form.name.trim() !== "" &&
        this.form.price_sale &&
        this.form.price_sale > 0
      );
    },

    showOnlineMedia() {
      return this.storeJoinedOnline && this.branchJoinedOnline;
    },

    previewImageSrc() {
      if (this.imgFile instanceof File) {
        return URL.createObjectURL(this.imgFile);
      }
      if (typeof this.imgFile === "string" && this.imgFile) {
        return this.imgFile;
      }
      return null;
    },
    previewCategoryName() {
      if (!this.form.category_id) return null;
      if (typeof this.form.category_id === "object") {
        return this.form.category_id.la_name || this.form.category_id.name || null;
      }
      const cat = (this.categories || []).find((c) => c.id === this.form.category_id);
      return cat ? cat.la_name || cat.name : null;
    },
    previewUnitName() {
      if (!this.form.size_id) return null;
      if (typeof this.form.size_id === "object") {
        return this.form.size_id.name || null;
      }
      const s = (this.sizes || []).find((size) => size.id === this.form.size_id);
      return s ? s.name : null;
    },

    // Create a temporary product object for barcode preview
    previewProduct() {
      return {
        code: this.form.code || "",
        name: this.form.name || "",
        name_en: this.form.name_en || "",
        name_th: this.form.name_th || "",
        name_cn: this.form.name_cn || "",
        price_sale: this.price_sale || 0,
        currency: this.currencies.find(
          (c) => c.id === this.form.currency_id,
        ) || {
          code: "LAK",
        },
        size: this.form.size_id || null,
        category: this.form.category_id || null,
        img: this.imgFile || null,
      };
    },

    // ---- redesigned header/name/checklist helpers ----
    langTabs() {
      return ["LA", "EN", "TH", "CN"].map((code) => ({
        code,
        filled: !!(
          this.langFieldValue(code) && this.langFieldValue(code).trim()
        ),
      }));
    },
    activeLangKey() {
      return this.activeLang === "LA"
        ? "name"
        : `name_${this.activeLang.toLowerCase()}`;
    },
    currentNameValue: {
      get() {
        return this.form[this.activeLangKey] || "";
      },
      set(val) {
        this.$set(this.form, this.activeLangKey, val);
      },
    },
    headline() {
      return (
        (this.form.name && this.form.name.trim()) ||
        (this.form.name_en && this.form.name_en.trim()) ||
        (this.form.name_th && this.form.name_th.trim()) ||
        (this.form.name_cn && this.form.name_cn.trim()) ||
        this.$t("untitled_product")
      );
    },
    readinessChecklist() {
      return [
        {
          key: "name",
          ok: !!(this.form.name && this.form.name.trim()),
          label: this.$t("product_name"),
        },
        {
          key: "price",
          ok: this.price_sale > 0 && this.price_sale > this.price_buy,
          label: this.$t("check_price_above_cost"),
        },
        {
          key: "classification",
          ok: !!this.form.category_id && !!this.form.size_id,
          label: this.$t("check_category_unit"),
        },
        {
          key: "image",
          ok: !!this.imgFile,
          label: this.$t("product_image"),
        },
      ];
    },
    readinessTotal() {
      return this.readinessChecklist.length;
    },
    readinessCount() {
      return this.readinessChecklist.filter((c) => c.ok).length;
    },
    readinessPercent() {
      return (this.readinessCount / this.readinessTotal) * 100;
    },
    footerReadinessText() {
      if (this.readinessCount === this.readinessTotal) {
        return this.$t("everything_required_filled");
      }
      return this.$t("items_left_before_saving", {
        count: this.readinessTotal - this.readinessCount,
      });
    },
    stockValue() {
      const stock = Number(this.form.stock) || 0;
      return stock * this.price_buy;
    },
    marginNote() {
      if (!this.price_sale) {
        return this.$t("enter_sale_price_hint");
      }
      const percent =
        ((this.price_sale - this.price_buy) / this.price_sale) * 100;
      return this.$t("margin_label", { percent: percent.toFixed(1) });
    },
  },
  created() {
    this.fetchCategory();
    this.fetchVendor();
    this.fetchSize();
  },
  mounted() {
    this.fetExchange();
    this.checkRoute("edit_stock");
    this.checkStoreOnlineJoin();
  },

  watch: {
    expiredPro(val) {
      if (val) {
        const expired_date = new Date(
          Date.now() - new Date().getTimezoneOffset() * 60000,
        )
          .toISOString()
          .substr(0, 10);
        this.form.expired_date = expired_date;
      } else {
        this.form.expired_date = null;
      }
    },
    hasVariations(val) {
      if (val) {
        // When variants are enabled, set stock and notify_stock to 0
        this.form.stock = 0;
        this.form.notify_stock = 0;

        // Note: track_stock can remain enabled for variants - stock will be managed at variant level
        // this.form.track_stock = false; // Removed automatic disabling

        // When variants are enabled, disable bundle functionality (mutual exclusivity)
        this.form.is_bundle = false;
        this.bundleItems = [];

        // When variants are enabled, set default prices if they are not null
        if (this.form.price_buy && this.form.price_buy > 0) {
          // Keep the current price_buy as default cost
        }
        if (this.form.price_sale && this.form.price_sale > 0) {
          // Keep the current price_sale as default price
        }

        // Update existing variants with current parent product data
        this.$nextTick(() => {
          if (this.$refs.productVariations) {
            this.$refs.productVariations.updateVariantsWithParentData();
          }
        });
      }
    },
    "form.price_buy"(val) {
      // Update variant costs when parent price_buy changes
      if (this.hasVariations && this.$refs.productVariations) {
        this.$refs.productVariations.updateVariantsWithParentData();
      }
    },
    "form.price_sale"(val) {
      // Update variant prices when parent price_sale changes
      if (this.hasVariations && this.$refs.productVariations) {
        this.$refs.productVariations.updateVariantsWithParentData();
      }
    },
    "form.code"(val) {
      // Update variant barcodes when parent code changes
      if (this.hasVariations && this.$refs.productVariations) {
        this.$refs.productVariations.updateVariantsWithParentData();
      }
    },
    bundleItems: {
      handler(val) {
        // Automatically set is_bundle flag based on whether there are bundle items
        this.form.is_bundle = val && val.length > 0;

        // When bundle is enabled, set stock and notify_stock to 0
        if (this.form.is_bundle) {
          this.form.stock = 0;
          this.form.notify_stock = 0;
          // When bundle is enabled, disable track_stock as stock is managed at bundle level
          this.form.track_stock = false;
          // When bundle is enabled, disable variants functionality (mutual exclusivity)
          this.hasVariations = false;
          this.form.has_variations = false;

          // When bundle is enabled, disable expiration functionality
          this.expiredPro = false;
          this.form.expired_date = null;

          // Calculate initial bundle price
          this.calculateBundlePrice();
        }
      },
      deep: true,
    },

    // Watch for direct changes to form.is_bundle
    "form.is_bundle"(val) {
      if (val) {
        // When bundle is enabled, disable variants
        this.hasVariations = false;
        this.form.has_variations = false;
        this.form.stock = 0;
        this.form.notify_stock = 0;
        this.form.track_stock = false;

        // When bundle is enabled, disable expiration functionality
        this.expiredPro = false;
        this.form.expired_date = null;
      }
    },

    // Watch for direct changes to form.has_variations
    "form.has_variations"(val) {
      if (val) {
        // When variants are enabled, disable bundle and dynamic pricing
        this.form.is_bundle = false;
        this.bundleItems = [];
        this.form.stock = 0;
        this.form.notify_stock = 0;
        this.form.dynamic_pricing = false;
        // Note: track_stock can remain enabled for variants - stock will be managed at variant level
        // this.form.track_stock = false; // Removed automatic disabling
      }
    },
    // Watch for hasVariations changes to disable dynamic_pricing
    hasVariations(val) {
      if (val) {
        this.form.dynamic_pricing = false;
      }
    },
  },

  methods: {
    ...mapActions({
      fetch: "index/fetch",
    }),
    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.push("/manage-stock");
      }
    },
    langFieldValue(code) {
      const key = code === "LA" ? "name" : `name_${code.toLowerCase()}`;
      return this.form[key];
    },
    async fetchVendor() {
      const body = {
        that: this,
        url: "suppliers",
        commit: "SET_SUPPLIERS",
      };
      await this.fetch(body);
    },
    async fetchSize() {
      const body = {
        that: this,
        url: "product_size",
        commit: "SET_SIZES",
      };
      await this.fetch(body);
    },
    async fetchCategory() {
      const body = {
        that: this,
        url: "categories",
        commit: "SET_CATEGORY",
      };
      await this.fetch(body);
    },
    openQuickCategoryModal() {
      this.quickCategoryForm = { la_name: "", en_name: "" };
      this.quickCategoryTouched = false;
      this.showQuickCategoryModal = true;
    },
    async handleQuickCategorySubmit() {
      this.quickCategoryTouched = true;
      if (!this.quickCategoryForm.la_name || !this.quickCategoryForm.la_name.trim()) {
        return;
      }
      this.quickCategorySubmitting = true;
      try {
        const payload = {
          la_name: this.quickCategoryForm.la_name.trim(),
          en_name: this.quickCategoryForm.en_name ? this.quickCategoryForm.en_name.trim() : null,
          is_online: 1,
          is_cooking: 1,
          number: 0,
        };
        const { data } = await this.$http.post("categories", payload);
        if (data && data.status) {
          this.showQuickCategoryModal = false;
          await this.fetchCategory();
          const created =
            this.categories.find((c) => c.id === data.data?.id) || data.data;
          this.form.category_id = created;
          if (this.$v && this.$v.form && this.$v.form.category_id) {
            this.$v.form.category_id.$touch();
          }
          await success(this, data.message);
        } else {
          await toast(this, data?.message || "error_occurred", "error");
        }
      } catch (error) {
        console.error("quick category create error", error);
        const message =
          error?.response?.data?.message ||
          error?.message ||
          this.$t("error_occurred") ||
          "Error";
        if (this.$toast) {
          this.$toast.error(message);
        } else {
          await toast(this, message, "error");
        }
      } finally {
        this.quickCategorySubmitting = false;
      }
    },
    async submitForm(form) {
      try {
        // Enforce mutual exclusivity before form submission
        this.enforceMutualExclusivity();

        this.$v.form.$touch();
        if (!this.$v.form.$invalid) {
          this.overlay = true;
          let formData = new FormData();
          formData.append("code", form.code);
          formData.append("name", form.name);
          formData.append("name_en", form.name_en || "");
          formData.append("name_th", form.name_th || "");
          formData.append("name_cn", form.name_cn || "");
          let options = [];
          let variants = [];

          if (this.hasVariations) {
            // Prioritize stored data since it's working correctly
            options = this.optionsData || [];
            variants = this.variantsData || [];

            // Fallback: Try to get current data from ProductSettings if stored data is empty
            if (
              !options ||
              options.length === 0 ||
              !variants ||
              variants.length === 0
            ) {
              if (this.$refs.productSettings) {
                const currentData =
                  this.$refs.productSettings.getCurrentVariationsData();

                // Use current data if available
                if (currentData.options && currentData.options.length > 0) {
                  options = currentData.options;
                }
                if (currentData.variants && currentData.variants.length > 0) {
                  variants = currentData.variants;
                }
              }
            }
          }

          // If variants exist, set price_buy and price_sale to 0
          if (variants.length > 0) {
            form.price_buy = 0;
            form.price_sale = 0;
            form.stock = 0;
            form.notify_stock = 0;
          }

          // If product is a bundle, set stock and notify_stock to 0
          if (form.is_bundle) {
            form.stock = 0;
            form.notify_stock = 0;
          }
          formData.append("desc", form.desc);
          if (this.expiredPro) {
            formData.append("expired_date", form.expired_date);
          }
          formData.append("notify_stock", form.notify_stock);
          formData.append("price_buy", form.price_buy);
          formData.append("price_sale", form.price_sale);
          formData.append("stock", this.edit_stock ? form.stock : 0);
          formData.append("track_stock", form.track_stock ? 1 : 0);
          // Handle optional fields - only append if they have values
          if (form.vendor_id && form.vendor_id.id) {
            formData.append("vendor_id", form.vendor_id.id);
          }
          formData.append("category_id", form.category_id.id);
          if (form.size_id && form.size_id.id) {
            formData.append("size_id", form.size_id.id);
          }
          formData.append("currency_id", form.currency_id);
          // Add options and variants
          formData.append("options", JSON.stringify(options));
          formData.append("variants", JSON.stringify(variants));
          // Set has_variations based on whether variants exist
          formData.append(
            "has_variations",
            variants && variants.length > 0 ? 1 : 0,
          );
          formData.append(
            "has_topping",
            form.topping_ids && form.topping_ids.length > 0 ? 1 : 0,
          );
          formData.append(
            "topping_ids",
            JSON.stringify(form.topping_ids || []),
          );
          formData.append("is_bundle", form.is_bundle ? 1 : 0);
          if (form.is_bundle) {
            const payload = this.bundleItems.map((b) => ({
              product_id: b.product_id,
              quantity: b.quantity,
            }));

            formData.append("bundle_products", JSON.stringify(payload));
          }
          formData.append("dynamic_pricing", form.dynamic_pricing ? 1 : 0);
          formData.append(
            "channel_prices",
            JSON.stringify(form.channel_prices || []),
          );

          if (this.showOnlineMedia) {
            if (
              this.$refs.productOnlineMedia &&
              !this.$refs.productOnlineMedia.validate()
            ) {
              this.overlay = false;
              return;
            }

            this.galleryPendingFiles.forEach((file) => {
              formData.append("gallery_files[]", file);
            });
            formData.append("video_url", this.videoUrl || "");
            formData.append("is_online", form.is_online ? 1 : 0);
          }

          // Add image file if exists
          if (this.imgFile) {
            formData.append("file", this.imgFile);
          }

          const { data } = await this.$http.post(
            "add_products",
            formData,
            Help.headerFile(),
          );
          if (data.status && form.topping_ids && form.topping_ids.length > 0) {
            try {
              await this.$http.put(`products/${data.data.id}/toppings`, {
                topping_ids: form.topping_ids,
              });
            } catch (toppingErr) {
              console.error("Failed to sync product toppings:", toppingErr);
            }
          }
          this.overlay = false;
          if (data.status) {
            this.clearData();
            await success(this, data.message);
          }
        }
        return;
      } catch (err) {
        this.overlay = false;
      }
    },

    addBundleItem() {
      if (!this.bundleSearchResult) return;

      // Check if product already exists in bundle
      const exists = this.bundleItems.find(
        (x) => x.product_id === this.bundleSearchResult.id,
      );
      if (exists) return;

      // Check currency match for bundle products
      if (this.form.is_bundle && this.bundleItems.length > 0) {
        const firstProductCurrency = this.bundleItems[0].product.currency_id;
        if (this.bundleSearchResult.currency_id !== firstProductCurrency) {
          // Show warning about currency mismatch
          this.$swal({
            title:
              this.$t("currency_mismatch_warning") ||
              "Currency Mismatch Warning",
            text:
              this.$t("currency_mismatch_message") ||
              "Cannot add products with different currencies to a bundle. All bundle products must have the same currency.",
            icon: "warning",
            confirmButtonText: this.$t("ok") || "OK",
          });
          return;
        }
      }

      // Add product to bundle
      this.bundleItems.push({
        product_id: this.bundleSearchResult.id,
        quantity: this.bundleQty || 1,
        product: this.bundleSearchResult,
      });

      // Calculate total price_buy for bundle
      this.calculateBundlePrice();

      this.bundleSearchResult = null;
      this.bundleSearch = "";
      this.bundleQty = 1;
    },
    removeBundleItem(idx) {
      this.bundleItems.splice(idx, 1);

      // Recalculate total price_buy for bundle after removal
      this.calculateBundlePrice();
    },

    // Calculate total price_buy and price_sale for bundle products
    calculateBundlePrice() {
      if (this.form.is_bundle && this.bundleItems.length > 0) {
        let totalPriceBuy = 0;
        let totalPriceSale = 0;

        this.bundleItems.forEach((item) => {
          const productPriceBuy = parseFloat(item.product.price_buy || 0);
          const productPriceSale = parseFloat(item.product.price_sale || 0);
          const quantity = parseInt(item.quantity || 1);

          // Since backend now stores original prices, no conversion needed
          totalPriceBuy += productPriceBuy * quantity;
          totalPriceSale += productPriceSale * quantity;
        });

        // Update the parent product's price_buy and price_sale
        this.form.price_buy = totalPriceBuy;
        this.form.price_sale = totalPriceSale;
      }
    },

    // Get current exchange rate for the selected currency
    getCurrentExchangeRate() {
      if (!this.currencies || !Array.isArray(this.currencies)) return 1;
      const selectedCurrency = this.currencies.find(
        (currency) => currency.id === this.form.currency_id,
      );
      if (selectedCurrency && selectedCurrency.exchange_rate) {
        return Number(selectedCurrency.exchange_rate);
      }
      return 1;
    },
    async clearForm() {
      const { isConfirmed } = await confirm({
        props: this,
        text: "want_to_clear_form",
      });

      if (isConfirmed) {
        this.clearData();
      }
    },
    clearData() {
      this.form = {
        code: "",
        name: "",
        name_en: "",
        name_th: "",
        name_cn: "",
        desc: "",
        stock: 0,
        expired_date: today,
        notify_stock: 0,
        price_sale: 0,
        price_buy: 0,
        category_id: null,
        vendor_id: null,
        size_id: null,
        track_stock: false,
        currency_id: 1,
        is_bundle: false,
        has_variations: false, // Explicitly set this
        dynamic_pricing: false,
        is_online: true,
      };
      this.imgFile = null;
      this.galleryExisting = [];
      this.galleryPendingFiles = [];
      this.videoUrl = "";
      this.expiredPro = false;
      this.hasVariations = false;
      this.bundleItems = [];
      this.variantsData = [];
      this.optionsData = [];
      this.activeLang = "LA";
      if (
        this.$refs.productSettings &&
        this.$refs.productSettings.$refs.productVariations
      ) {
        this.$refs.productSettings.$refs.productVariations.clearData();
      }
      this.$nextTick(() => {
        this.$v.$reset();
      });
    },

    handleVariationsUpdated(variations) {
      // Store the variants and options data from ProductVariations component
      if (variations && Array.isArray(variations)) {
        this.variantsData = variations;

        // Get options from the ProductVariations component via ProductSettings
        if (
          this.$refs.productSettings &&
          this.$refs.productSettings.$refs.productVariations
        ) {
          this.optionsData =
            this.$refs.productSettings.$refs.productVariations.options;
        }
      }
    },
    async fetExchange() {
      try {
        const { data } = await this.$http.get("get_currency");
        this.currencies = data.data;
        // Default currency is always LAK, regardless of UI language
        const currency = this.currencies.find((c) => c.code == "LAK");
        if (currency) {
          this.form.currency_id = currency.id;
        } else {
          // Fallback to first currency if match not found
          if (this.currencies.length > 0) {
            this.form.currency_id = this.currencies[0].id;
          }
        }
      } catch (error) {
        // Handle error silently
      }
    },
    onFileSelected(file) {
      // File selected
    },
    onFileRemoved() {
      // File removed
    },

    async checkStoreOnlineJoin() {
      try {
        const { data } = await this.$http.post("check_join");
        this.storeJoinedOnline =
          !!data?.data && data.data.status === "approved";
        this.branchJoinedOnline =
          !!data?.data && Number(data.data.branch_is_online) === 1;
      } catch (error) {
        this.storeJoinedOnline = false;
        this.branchJoinedOnline = false;
      }
    },
    async checkRoute(role) {
      const _result = await this.hasRole(role);
      this.edit_stock = _result;
    },

    // Enforce mutual exclusivity between variants and bundles
    enforceMutualExclusivity() {
      if (this.form.has_variations && this.form.is_bundle) {
        // If both are enabled, prioritize variants and disable bundle
        this.form.is_bundle = false;
        this.bundleItems = [];
      }
    },

    // Handle manual toggle of has_variations
    onHasVariationsToggle() {
      if (this.form.has_variations) {
        // When variants are enabled, disable bundle
        this.form.is_bundle = false;
        this.bundleItems = [];
        this.form.stock = 0;
        this.form.notify_stock = 0;
        this.form.track_stock = false;
      }
    },

    // Handle manual toggle of is_bundle
    onIsBundleToggle() {
      if (this.form.is_bundle) {
        // When bundle is enabled, disable variants
        this.hasVariations = false;
        this.form.has_variations = false;
        this.form.stock = 0;
        this.form.notify_stock = 0;
        this.form.track_stock = false;
      }
    },

    generateBarcode() {
      const today = new Date().toISOString().substr(0, 8).replace(/-/g, "");
      const randomDigits = Math.floor(Math.random() * 1000)
        .toString()
        .padStart(3, "0");
      this.form.code = `${today}${randomDigits}`;
    },

    // Open barcode preview modal
    openQuickBarcodeModal() {
      // Validate that barcode exists
      if (!this.form.code) {
        this.$toast.error(
          this.$t("please_enter_barcode") || "Please enter a barcode first",
        );
        return;
      }
      this.showBarcodeModal = true;
    },

    // Handle print event from barcode modal
    handleBarcodePrint({ barcodes, settings }) {
      this.$toast.success(
        this.$t("barcode_printed_successfully") ||
          `Barcode printed successfully! (${barcodes.length} copies)`,
      );
    },
  },
};
</script>

<style scoped>
.product-page-title {
  font-size: 20px;
  font-weight: 900;
}

.product-card-title {
  font-size: 14px;
  font-weight: 500;
}
.v-card__title.product-card-title {
  font-weight: 500;
}

.section-label,
.v2-label,
.ap-card ::v-deep .ps-text-field > .pb-2,
.ap-card ::v-deep .ps-text-select > .pb-2,
.ap-card ::v-deep .ps-text-area > .pb-2 {
  font-size: 13.5px !important;
  font-weight: 700 !important;
  color: #1e293b !important;
  margin-bottom: 6px;
}

.required-mark {
  color: #ef4444;
  font-weight: 700;
}

/* header row: kicker + headline + readiness progress */
.ap-header-row {
  gap: 20px;
}
.ap-kicker {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  color: var(--v-primary-base, #5b39c9);
}
.ap-progress {
  min-width: 200px;
}
.ap-progress__labels {
  font-size: 11.5px;
  color: #94a3b8;
  margin-bottom: 4px;
}

/* top-level section cards: slightly larger, consistent radius than Vuetify's rounded-lg */
.ap-card {
  border-radius: 16px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  overflow: hidden;
}
.theme--dark .ap-card,
.theme--dark.ap-card {
  background: #1e1e2d !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}

/* Advanced settings panel (ProductSettings + ProductBundleBuilder) — CSS-only chrome so it
   reads consistently with the other section cards on this page. ProductSettings.vue itself
   (variants/bundle/expiry/pricing logic) is untouched; only its rendered DOM is restyled. */
.ap-settings-wrap ::v-deep > .v-card {
  border-radius: 16px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
}
.theme--dark .ap-settings-wrap ::v-deep > .v-card {
  background: #1e1e2d !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}
.ap-settings-wrap ::v-deep > .v-card > .v-card__title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 500;
}
.ap-settings-wrap ::v-deep > .v-card > .v-card__title .ap-card-accent {
  width: 3px;
  height: 15px;
  border-radius: 2px;
  background: var(--v-primary-base, #5b39c9);
  display: inline-block;
}
.ap-settings-wrap ::v-deep > .v-card > .v-card__title .ap-card-subtitle {
  font-size: 11.5px;
  font-weight: 400;
  color: #94a3b8;
}
/* flatten the inner feature cards (currency / expiry / inventory / variants / bundle /
   pricing) into simple plain rows instead of individually boxed cards — no divider
   lines between them.
   Vuetify 2 renders the `outlined` prop as the `v-sheet--outlined` mixin class, not
   `v-card--outlined` — this must match that class or the override never applies. */
.ap-settings-wrap ::v-deep .v-sheet--outlined .v-sheet--outlined {
  border: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  margin: 0 !important;
  background: transparent !important;
}
.theme--dark .ap-settings-wrap ::v-deep .v-sheet--outlined .v-sheet--outlined {
  border: none !important;
  background: transparent !important;
}
.ap-settings-wrap
  ::v-deep
  .v-sheet--outlined
  .v-sheet--outlined
  > .v-card__title {
  font-size: 13px;
  font-weight: 900;
  padding: 14px 4px 6px;
}
.ap-settings-wrap
  ::v-deep
  .v-sheet--outlined
  .v-sheet--outlined
  > .v-card__text {
  padding: 14px 4px;
}
/* Currency and Inventory inside ProductSettings have their own
   dedicated cards on the left panel (Price, Inventory) — hide these duplicate rows */
.ap-settings-wrap ::v-deep .product-settings-currency,
.ap-settings-wrap ::v-deep .product-settings-stock {
  display: none !important;
}

/* Input Fields Appearance (Ref: /manage-promotions/new) */
.ap-card ::v-deep .v-text-field--outlined .v-input__slot,
.ap-card ::v-deep .v-textarea--outlined .v-input__slot {
  background: #f8fafc !important;
  border-radius: 12px !important;
  min-height: 44px;
  border: 1px solid #e2e8f0 !important;
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.ap-card ::v-deep .v-text-field--outlined fieldset,
.ap-card ::v-deep .v-textarea--outlined fieldset {
  border: none !important;
}

.ap-card ::v-deep .v-text-field--outlined.v-input--is-focused .v-input__slot,
.ap-card ::v-deep .v-textarea--outlined.v-input--is-focused .v-input__slot {
  background: #ffffff !important;
  border-color: var(--v-primary-base, #5b39c9) !important;
  box-shadow: 0 0 0 3px rgba(91, 57, 201, 0.12) !important;
}

.ap-card ::v-deep .v-text-field--outlined .v-input__slot input,
.ap-card ::v-deep .v-select__selection,
.ap-card ::v-deep .v-textarea--outlined .v-input__slot textarea {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}

.ap-card ::v-deep .v-input__append-inner {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  align-self: center !important;
}

/* Barcode generate button */
.barcode-gen-btn {
  width: 28px !important;
  height: 28px !important;
  background: rgba(91, 57, 201, 0.08) !important;
  color: var(--v-primary-base, #5b39c9) !important;
}
.barcode-gen-btn:hover {
  background: rgba(91, 57, 201, 0.16) !important;
}

/* read-only stock-value box (Inventory card) */
.ap-readonly-box {
  display: flex;
  align-items: center;
  padding: 0 14px;
  border-radius: 12px;
  border: 1px dashed rgba(15, 23, 42, 0.16);
  background: #f8fafc;
  font-size: 14px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #475569;
  min-height: 44px;
  box-sizing: border-box;
}
::v-deep .theme--dark .ap-readonly-box {
  border-color: rgba(255, 255, 255, 0.16);
  background: #262638;
  color: rgba(226, 232, 240, 0.9);
}

/* card title with accent bar + subtitle */
.ap-card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.ap-card-accent {
  width: 3px;
  height: 15px;
  border-radius: 2px;
  background: var(--v-primary-base, #5b39c9);
  display: inline-block;
}
.ap-card-subtitle {
  font-size: 11.5px;
  font-weight: 400;
  color: #94a3b8;
}

.ap-hint {
  font-size: 10.5px;
  color: #94a3b8;
  display: inline-flex;
  align-items: center;
}

/* identity: compact image + name side by side */
.ap-identity-row {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  flex-wrap: wrap;
}
.ap-identity-row__image {
  width: 220px;
  flex: 0 0 220px;
}
.ap-identity-row__name {
  flex: 1 1 320px;
  min-width: 280px;
}
@media (max-width: 599px) {
  .ap-identity-row__image {
    width: 100%;
    flex: 1 1 100%;
  }
}

/* language tab pills */
.ap-lang-pills {
  display: inline-flex;
  gap: 4px;
  padding: 3px;
  border-radius: 8px;
  background: rgba(91, 57, 201, 0.06);
  border: 1px solid rgba(15, 23, 42, 0.08);
  width: fit-content;
}
.ap-lang-pill {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 11px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  user-select: none;
  color: #64748b;
  transition: color 0.15s, background 0.15s;
}
.ap-lang-pill--active {
  color: var(--v-primary-base, #5b39c9);
  background: rgba(91, 57, 201, 0.12);
}
.ap-lang-dot {
  width: 5px;
  height: 5px;
  border-radius: 999px;
  background: #cbd5e1;
  display: block;
}
.ap-lang-dot--filled {
  background: var(--v-primary-base, #5b39c9);
}

/* before-you-save checklist */
.ap-checklist-title {
  font-size: 10.5px;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: #94a3b8;
  font-weight: 700;
  margin-bottom: 8px;
}
.ap-checklist {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ap-checklist__row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
}
.ap-checklist__mark {
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #cbd5e1;
}
.ap-checklist__mark--ok {
  background: var(--v-primary-base, #5b39c9);
  border-color: var(--v-primary-base, #5b39c9);
}
.ap-checklist__label {
  color: #94a3b8;
}
.ap-checklist__label--ok {
  color: #334155;
  font-weight: 600;
}

/* v2 toggle row */
.v2-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  padding: 10px 14px;
}
/* borderless variant: switch + label inline, no box (used in Availability card) */
.ap-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.ap-hint--link {
  color: var(--v-primary-base, #5b39c9);
  cursor: pointer;
  font-weight: 700;
}
.ap-hint--link:hover {
  text-decoration: underline;
}
.v2-toggle-row__text {
  min-width: 0;
}
.v2-toggle-title {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
}
.v2-toggle-desc {
  margin: 2px 0 0;
  font-size: 11px;
  color: #94a3b8;
  font-weight: 400;
}

/* v2 profit bar */
.v2-profit {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: rgba(91, 57, 201, 0.08);
  border-radius: 12px;
  padding: 10px 14px;
}
.v2-profit__info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.v2-profit__label {
  font-size: 10.5px;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
}
.v2-profit__margin {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
}
.v2-profit__value {
  font-size: 17px;
  font-weight: 900;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* dark mode tweaks */
::v-deep .theme--dark .v2-label,
::v-deep .theme--dark .v2-toggle-desc,
::v-deep .theme--dark .ap-progress__labels,
::v-deep .theme--dark .ap-card-subtitle,
::v-deep .theme--dark .ap-hint,
::v-deep .theme--dark .ap-checklist-title,
::v-deep .theme--dark .ap-checklist__label {
  color: rgba(148, 163, 184, 0.9);
}
::v-deep .theme--dark .v2-toggle-row,
::v-deep .theme--dark .ap-lang-pills {
  border-color: rgba(255, 255, 255, 0.12);
}
::v-deep .theme--dark .ap-lang-pills {
  background: rgba(139, 111, 240, 0.12);
}
::v-deep .theme--dark .ap-lang-pill {
  color: rgba(148, 163, 184, 0.9);
}
::v-deep .theme--dark .ap-lang-pill--active,
::v-deep .theme--dark .ap-kicker {
  color: #8b6ff0;
}
::v-deep .theme--dark .ap-lang-pill--active {
  background: rgba(139, 111, 240, 0.2);
}
::v-deep .theme--dark .ap-card-accent,
::v-deep .theme--dark .ap-checklist__mark--ok {
  background: #8b6ff0;
}
::v-deep .theme--dark .ap-checklist__mark--ok {
  border-color: #8b6ff0;
}
::v-deep .theme--dark .ap-checklist__label--ok {
  color: #e2e8f0;
}
::v-deep .theme--dark .v2-profit {
  background: rgba(91, 57, 201, 0.2);
}
::v-deep .theme--dark .v2-profit__label,
::v-deep .theme--dark .v2-profit__margin {
  color: rgba(148, 163, 184, 0.9);
}

/* Dark Mode Input Fields Appearance */
::v-deep .theme--dark .section-label,
::v-deep .theme--dark .ps-text-field > .pb-2,
::v-deep .theme--dark .ps-text-select > .pb-2,
::v-deep .theme--dark .ps-text-area > .pb-2 {
  color: rgba(255, 255, 255, 0.88) !important;
}

::v-deep .theme--dark.ap-card .v-text-field--outlined .v-input__slot,
::v-deep .theme--dark.ap-card .v-textarea--outlined .v-input__slot,
::v-deep .theme--dark .ap-card .v-text-field--outlined .v-input__slot,
::v-deep .theme--dark .ap-card .v-textarea--outlined .v-input__slot {
  background: #262638 !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
}

::v-deep .theme--dark.ap-card .v-text-field--outlined.v-input--is-focused .v-input__slot,
::v-deep .theme--dark.ap-card .v-textarea--outlined.v-input--is-focused .v-input__slot,
::v-deep .theme--dark .ap-card .v-text-field--outlined.v-input--is-focused .v-input__slot,
::v-deep .theme--dark .ap-card .v-textarea--outlined.v-input--is-focused .v-input__slot {
  background: #262638 !important;
  border-color: #8b6ff0 !important;
  box-shadow: 0 0 0 3px rgba(139, 111, 240, 0.2) !important;
}

::v-deep .theme--dark.ap-card .v-text-field--outlined .v-input__slot input,
::v-deep .theme--dark.ap-card .v-select__selection,
::v-deep .theme--dark.ap-card .v-textarea--outlined .v-input__slot textarea,
::v-deep .theme--dark .ap-card .v-text-field--outlined .v-input__slot input,
::v-deep .theme--dark .ap-card .v-select__selection,
::v-deep .theme--dark .ap-card .v-textarea--outlined .v-input__slot textarea {
  color: #f1f5f9 !important;
}

::v-deep .theme--dark .barcode-gen-btn {
  background: rgba(139, 111, 240, 0.15) !important;
  color: #c4b5fd !important;
}

/* Header & Back Button */
.ap-back-btn {
  width: 40px !important;
  height: 40px !important;
  min-width: 40px !important;
  padding: 0 !important;
  border-color: rgba(91, 57, 201, 0.25) !important;
  background: rgba(91, 57, 201, 0.04) !important;
}
.ap-back-btn:hover {
  background: rgba(91, 57, 201, 0.1) !important;
}
::v-deep .theme--dark .ap-back-btn {
  border-color: rgba(139, 111, 240, 0.3) !important;
  background: rgba(139, 111, 240, 0.08) !important;
}

/* Enable Switch Row */
.enable-switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 1px solid #e2e8f0;
}
.bg-slate-50 {
  background: #f8fafc !important;
}
::v-deep .theme--dark .enable-switch-row {
  border-color: rgba(255, 255, 255, 0.08);
}
::v-deep .theme--dark .bg-slate-50 {
  background: rgba(255, 255, 255, 0.04) !important;
}

/* Live Product Preview Card */
.ps-product-preview-card {
  border-radius: 14px;
  border: 1px solid rgba(91, 57, 201, 0.18);
  background: linear-gradient(180deg, rgba(91, 57, 201, 0.03) 0%, rgba(255, 255, 255, 0.8) 100%);
  padding: 14px;
}
.ps-product-preview-brand {
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: var(--v-primary-base, #5b39c9);
}
.ps-product-preview-readiness {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  background: rgba(0, 0, 0, 0.04);
  padding: 2px 8px;
  border-radius: 10px;
}
.ps-product-preview-avatar {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: rgba(91, 57, 201, 0.08);
  border: 1px dashed rgba(91, 57, 201, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}
.ps-product-preview-title {
  font-size: 15px;
  line-height: 1.3;
  color: #1e293b;
}
.ps-product-preview-code {
  font-size: 11px;
  font-family: monospace;
  font-weight: 700;
  color: #475569;
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
}
.ps-product-preview-divider {
  border-top: 1px dashed #cbd5e1;
}
.ps-product-preview-footer {
  border-top-color: #e2e8f0 !important;
}

::v-deep .theme--dark .ps-product-preview-card {
  border-color: rgba(139, 111, 240, 0.25);
  background: linear-gradient(180deg, rgba(139, 111, 240, 0.06) 0%, rgba(30, 30, 45, 0.9) 100%);
}
::v-deep .theme--dark .ps-product-preview-brand {
  color: #8b6ff0;
}
::v-deep .theme--dark .ps-product-preview-readiness {
  color: #94a3b8;
  background: rgba(255, 255, 255, 0.08);
}
::v-deep .theme--dark .ps-product-preview-avatar {
  background: rgba(139, 111, 240, 0.12);
  border-color: rgba(139, 111, 240, 0.3);
}
::v-deep .theme--dark .ps-product-preview-title {
  color: #f1f5f9;
}
::v-deep .theme--dark .ps-product-preview-code {
  color: #cbd5e1;
  background: rgba(255, 255, 255, 0.08);
}
::v-deep .theme--dark .ps-product-preview-divider {
  border-top-color: rgba(255, 255, 255, 0.12);
}
::v-deep .theme--dark .ps-product-preview-footer {
  border-top-color: rgba(255, 255, 255, 0.08) !important;
}

/* Primary Action Button */
.form-action-btn-primary {
  height: 44px !important;
  font-size: 14px;
  font-weight: 700;
}
</style>
